import React, { createContext, useContext, useState, useCallback } from 'react';
import { Snackbar, Alert } from '@mui/material';
import { colors } from '../../styles/colors';

const SnackbarContext = createContext(null);

export const useSnackbar = () => {
  const ctx = useContext(SnackbarContext);
  if (!ctx) throw new Error('useSnackbar must be used within SnackbarProvider');
  return ctx;
};

export const SnackbarProvider = ({ children }) => {
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });

  const showSuccess = useCallback((message) => {
    setSnackbar({ open: true, message, severity: 'success' });
  }, []);

  const showError = useCallback((message) => {
    setSnackbar({ open: true, message, severity: 'error' });
  }, []);

  const showInfo = useCallback((message) => {
    setSnackbar({ open: true, message, severity: 'info' });
  }, []);

  const handleClose = useCallback((_, reason) => {
    if (reason === 'clickaway') return;
    setSnackbar((prev) => ({ ...prev, open: false }));
  }, []);

  const duration = snackbar.severity === 'error' ? 6000 : 4000;

  return (
    <SnackbarContext.Provider value={{ showSuccess, showError, showInfo }}>
      {children}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={duration}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleClose}
          severity={snackbar.severity}
          variant="filled"
          sx={{
            borderRadius: '12px',
            backdropFilter: 'blur(8px)',
            fontFamily: '"DM Sans", sans-serif',
            fontWeight: 500,
            minWidth: 280,
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
            ...(snackbar.severity === 'success' && {
              bgcolor: colors.success,
            }),
            ...(snackbar.severity === 'error' && {
              bgcolor: colors.danger,
            }),
            ...(snackbar.severity === 'info' && {
              bgcolor: colors.info,
            }),
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </SnackbarContext.Provider>
  );
};

export default SnackbarProvider;
